import moment from 'moment';

import Logger from 'wanderu-logger';

import I18nDataStore from '../libs/i18nDataStore/i18nDataStore';
import I18next from '../libs/i18next';
import CurrencyCoordinator from './CurrencyCoordinator/CurrencyCoordinator';
import LocaleCoordinator from './LocaleCoordinator/LocaleCoordinator';

const logger = Logger.getLogger('i18nCoordinator');

class I18nCoordinator {
    constructor() {
        this.i18nDataStore = new I18nDataStore();
        this.i18next = new I18next();
        this.currencyCoordinator = new CurrencyCoordinator(this.i18nDataStore);
        this.localeCoordinator = new LocaleCoordinator(this.i18nDataStore, this.i18next);
    }

    toJson() {
        return this.i18nDataStore.toJson();
    }

    init(opts) {
        const {
            currency,
            defaultCurrency,
            defaultLocale,
            locale,
            localeToCurrencyMap,
        } = opts;

        this.currencyCoordinator.init({
            defaultCurrency: defaultCurrency,
            currency: currency || defaultCurrency,
            localeToCurrencyMap,
        });

        this.localeCoordinator.init({
            defaultLocale: defaultLocale,
            locale: locale || defaultLocale,
        });

        this.i18nDataStore.setLocaleToCurrencyMap(localeToCurrencyMap);
    }

    async initI18nLibraries(locale, currency) {
        const localePromise = this.localeCoordinator.initI18nLibraries(locale);
        const currencyPromise = this.currencyCoordinator.initI18nLibraries(currency);

        return await Promise.all([localePromise, currencyPromise]);
    }

    hydrate(opts) {
        const {
            translations,
            supportedCurrencies,
            currencyConversionRates,
            ...initOpts
        } = opts;

        this.init(initOpts);

        this.localeCoordinator.hydrate(translations);
        this.currencyCoordinator.hydrate(supportedCurrencies, currencyConversionRates);
    }

    updateTranslations(locale, translations, namespace) {
        this.localeCoordinator.updateTranslations(locale, translations, namespace);
    }

    refetchTranslations(namespace) {
        this.localeCoordinator.refetchTranslations(namespace);
    }

    registerListeners(opts) {
        const {
            onCurrencyChange,
            onLocaleChange,
            onSupportedLocalesChange,
            onSupportedCurrenciesChange,
        } = opts;

        this.currencyCoordinator.registerListeners({
            onCurrencyChange,
            onSupportedCurrenciesChange,
        });

        this.localeCoordinator.registerListeners({
            onLocaleChange,
            onSupportedLocalesChange,
        });
    }

    unregisterListeners() {
        this.localeCoordinator.unregisterListeners();
        this.currencyCoordinator.unregisterListeners();
    }

    async registerTranslatableApp(appConfig) {
        try {
            this.i18nDataStore.addAppConfig(appConfig);
        } catch (ex) {
            logger.warn('registerTranslatableApp', ex);

            return;
        }

        const currencyUpdatePromise = this.currencyCoordinator.update(appConfig);
        const localeUpdatePromise = this.localeCoordinator.update(appConfig);
        await Promise.all([
            currencyUpdatePromise,
            localeUpdatePromise,
        ]);
    }

    unregisterTranslatableApp(namespace) {
        try {
            this.i18nDataStore.removeAppConfig(namespace);
        } catch (ex) {
            logger.warn('unregisterTranslatableApp', ex);
            return;
        }

        this.currencyCoordinator.update();
        this.localeCoordinator.update();
    }

    // Should this logic live in i18nRedirect?
    update = async ({ locale, currency }) => {
        const currentLocale = this.i18nDataStore.getLocale();
        const currentCurrency = this.i18nDataStore.getCurrency();

        if (!locale || locale === currentLocale) {
            return this.updateCurrency(currency);
        }

        const defaultCurrency = this.i18nDataStore.getDefaultCurrency(locale);
        const currencyToSet = currency || defaultCurrency;

        let localeUpdatePromise = this.localeCoordinator.updateLocale(locale, {
            silent: true,
        });

        let currencyUpdatePromise = Promise.resolve();
        if (currencyToSet !== currentCurrency) {
            currencyUpdatePromise = this.currencyCoordinator.updateCurrency(currencyToSet, {
                silent: true,
            });
        }

        await Promise.all([
            localeUpdatePromise,
            currencyUpdatePromise,
        ]);

        if (locale) {
            this.localeCoordinator.notifyChangeListeners(locale, currentLocale, currencyToSet);
        }
    };

    updateLocale = async (locale) => {
        const currentLocale = this.i18nDataStore.getLocale();
        const defaultCurrency = this.i18nDataStore.getDefaultCurrency(locale);
        const defaultLocale = this.i18nDataStore.getDefaultLocale();
        const localeToSet = locale || defaultLocale;
        const localeUpdatePromise = this.localeCoordinator.updateLocale(localeToSet, {
            silent: true,
        });
        const currencyUpdatePromise = this.currencyCoordinator.updateCurrency(defaultCurrency, {
            silent: true,
        });
        await Promise.all([
            localeUpdatePromise,
            currencyUpdatePromise,
        ]);

        this.localeCoordinator.notifyChangeListeners(locale, currentLocale, defaultCurrency);
    };

    updateCurrency = async (currency) => {
        const defaultCurrency = this.i18nDataStore.getDefaultCurrency();
        const currencyToSet = currency || defaultCurrency;
        await this.currencyCoordinator.updateCurrency(currencyToSet);
    };

    updateDefaultCurrency(defaultCurrency) {
        this.i18nDataStore.setDefaultCurrency(defaultCurrency);
    }

    addLocaleChangeListener(callback) {
        const handle = this.i18nDataStore.addLocaleChangeListener(callback);
        return handle;
    }

    removeLocaleChangeListener(handle) {
        this.i18nDataStore.removeLocaleChangeListener(handle);
    }

    addCurrencyChangeListener(callback) {
        const handle = this.i18nDataStore.addCurrencyChangeListener(callback);
        return handle;
    }

    removeCurrencyChangeListener(handle) {
        this.i18nDataStore.removeCurrencyChangeListener(handle);
    }

    getI18nextInstance() {
        return this.i18next;
    }

    /* please do not use this */
    __forceImportLocaleData = async function(locale) {
        if (typeof window === undefined) {
            return;
        }

        moment.updateLocale(locale, window.moment().locale(locale).localeData()._config);
    };
}

function createI18nCoordinator() {
    return new I18nCoordinator();
}

export {
    I18nCoordinator,
    createI18nCoordinator,
};

export default new I18nCoordinator();
